import React, { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { setDemoAnonymous } from '../lib/session';

const DEMO_TENANT_CARD_ID = 'CONDOCARD-a-0001';
export const PROPERTY_DEMO_ORG_ID = 'condo-demo-org-0001';
export const TENANT_OFFER_FLAG = 'yulontap_property_demo_offer_tenant';

/**
 * A DEMO IS ONE CONTINUOUS STORY, so re-entering Tenant must RESUME it.
 *
 * This entry used to mint a session and `localStorage.setItem(d.store_key, …)`
 * on EVERY entry — the same defect that lost the hotel guest. A presenter who
 * registered their own unit (their name, their PIN, their language, the screen
 * they were on) had it overwritten by the synthetic demo resident on the next
 * Tenant tap, and if the launcher's `store_key` guess disagreed with the key the
 * portal derives from /info, the store held nothing for that key and the tenant
 * was dumped back on the unit-registration screen.
 *
 * Now: an existing tenant stay in this browser is validated and REUSED,
 * untouched. Only "Reset demo" starts a new one.
 */
const findLiveStay = async (api, cardId) => {
    let keys = [];
    try {
        keys = Object.keys(localStorage).filter((k) => k.startsWith('yulontap_stay:')
            || k === `yulontap_assistant:${cardId}`);
    } catch (_e) { return null; }
    for (const k of keys) {
        let token = null;
        try { token = (JSON.parse(localStorage.getItem(k)) || {}).token; } catch (_e) { /* skip */ }
        if (!token) continue;
        try {
            const r = await fetch(`${api}/api/assistant/${cardId}/session/${token}`);
            if (r.ok) return { key: k, token };
        } catch (_e) { /* try the next one */ }
    }
    return null;
};

export default function DemoTenantEntry() {
    const { t } = useTranslation();

    useEffect(() => {
        const API = (typeof window !== 'undefined' ? window.location.origin : '');
        const returnUrl = new URLSearchParams(window.location.search).get('return_url') || '';
        (async () => {
            try {
                // Isolate the tenant demo: NEVER inherit a manager/admin session
                // that may exist in this browser (that would route into
                // Operational Base). Mask it for THIS TAB only — deleting it
                // used to log the real admin out for good.
                setDemoAnonymous();
                sessionStorage.removeItem('yulontap_demo_mode');
                sessionStorage.removeItem('yulontap_hotel_demo');
                sessionStorage.removeItem('yulontap_auto_demo');
                if (returnUrl) sessionStorage.setItem('yulontap_demo_return', returnUrl);
                else sessionStorage.removeItem('yulontap_demo_return');

                const live = await findLiveStay(API, DEMO_TENANT_CARD_ID);
                if (live) {
                    // Same tenant, same unit, same requests. Land on their HOME
                    // (where request statuses are listed), not the sub-screen
                    // they left and not the "welcome back" interstitial.
                    try {
                        const cur = JSON.parse(localStorage.getItem(live.key)) || {};
                        localStorage.setItem(live.key, JSON.stringify({ ...cur, phase: 'menu', switchResume: true }));
                    } catch (_e) { /* the stay still resumes */ }
                    sessionStorage.removeItem(TENANT_OFFER_FLAG);
                    window.location.replace(`/assistant/${DEMO_TENANT_CARD_ID}`);
                    return;
                }
                // No tenant yet: land on the REAL registration screen, which
                // offers one-tap "Continue as demo tenant". The portal writes
                // the stay itself, using the org from its own /info call, so the
                // store key can never disagree with what it reads.
                sessionStorage.setItem(TENANT_OFFER_FLAG, '1');
                window.location.replace(`/assistant/${DEMO_TENANT_CARD_ID}`);
            } catch (e) {
                try {
                    setDemoAnonymous();
                    sessionStorage.removeItem('yulontap_demo_mode');
                } catch (_e) { /* ignore */ }
                window.location.replace(`/assistant/${DEMO_TENANT_CARD_ID}`);
            }
        })();
    }, []);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-950 text-slate-200"
            data-testid="demo-tenant-entry">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
            <p className="text-sm">{t('demo.enteringTenant', 'Opening resident portal…')}</p>
        </div>
    );
}
